import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Paperclip, Trash2, Upload, FileText, Image as ImageIcon, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShiftAttachments
 * Admin: upload/delete/list attachments, toggle visibility.
 * Client/Talent: consumed separately via /api/admin/shifts/{id}/attachments-for-me.
 */
const ShiftAttachments = ({ shiftId, adminMode = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const path = adminMode
        ? `/admin/shifts/${shiftId}/attachments`
        : `/admin/shifts/${shiftId}/attachments-for-me`;
      const res = await api.get(path);
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (shiftId) load(); /* eslint-disable-next-line */ }, [shiftId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('Max 15MB'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post(`/admin/shifts/${shiftId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { visible_to_client: true, visible_to_talent: true },
      });
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await api.delete(`/admin/shifts/${shiftId}/attachments/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const iconFor = (ct = '') => ct.startsWith('image/')
    ? <ImageIcon className="w-4 h-4 text-blue-500" />
    : <FileText className="w-4 h-4 text-red-500" />;

  return (
    <div className="space-y-3" data-testid="shift-attachments">
      {adminMode && (
        <div>
          <input
            type="file"
            ref={fileInput}
            onChange={onUpload}
            className="hidden"
            data-testid="attachment-file-input"
            accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.txt"
          />
          <Button
            size="sm"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="upload-attachment-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            {uploading ? 'Uploading…' : 'Upload Attachment'}
          </Button>
          <span className="text-xs text-gray-500 ml-2">PDF, images, documents. Max 15MB.</span>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-gray-400">No attachments yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between p-2.5 border border-gray-200 rounded-lg bg-white hover:border-cyan-300"
              data-testid="attachment-row"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />
                {iconFor(a.content_type)}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{a.file_name}</div>
                  <div className="text-[11px] text-gray-500">{a.size_kb}KB · {a.content_type || 'file'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}${a.file_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-cyan-700 hover:underline flex items-center gap-1"
                  data-testid="attachment-view-link"
                  onClick={async (e) => {
                    // We need auth header for this; open via fetch+blob for admin/client/talent
                    e.preventDefault();
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${a.file_url}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) throw new Error(`Status ${res.status}`);
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                      setTimeout(() => URL.revokeObjectURL(url), 10000);
                    } catch (err) {
                      toast.error(`Failed to open (${err.message})`);
                    }
                  }}
                >
                  <Eye className="w-3.5 h-3.5" /> View
                </a>
                {adminMode && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(a.id)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="attachment-delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShiftAttachments;
